import React from 'react';
import {
  Building2,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Plus,
  XCircle,
} from 'lucide-react';
import { VariantPanel } from './VariantPanel';
import { EmailReadyBadge } from './EmailReadyBadge';

export interface GroupProduct {
  id: string;
  name: string;
  manufacturer_id: string;
  manufacturer_name: string;
  campaign_name?: string;
  client_company?: string;
  is_active: boolean;
  // Marketing copy for follow-up emails; only set once maintained in the product form.
  tagline?: string | null;
  description?: string | null;
  claims?: string[] | null;
  nutrition_note?: string | null;
  base_note?: string | null;
  ordering_channels?: string | null;
  // Aggregates from the list endpoint, used for the email readiness badge.
  active_variant_count?: number | string;
  variant_image_count?: number | string;
}

export interface ManufacturerGroupData {
  manufacturerId: string;
  manufacturerName: string;
  products: GroupProduct[];
}

interface ManufacturerGroupProps {
  group: ManufacturerGroupData;
  layout: 'desktop' | 'mobile';
  expandedProductId: string | null;
  onToggleExpand: (productId: string) => void;
  onAddProduct: (manufacturerId: string) => void;
  renderActions: (product: GroupProduct) => React.ReactNode;
  /** Called after a variant or packshot changed, so the readiness badge stays truthful. */
  onVariantsChanged?: () => void;
}

interface RowProps {
  product: GroupProduct;
  expanded: boolean;
  onToggleExpand: (productId: string) => void;
  renderActions: (product: GroupProduct) => React.ReactNode;
  onVariantsChanged?: () => void;
}

function EmptyState({
  manufacturerId,
  onAddProduct,
}: {
  manufacturerId: string;
  onAddProduct: (id: string) => void;
}) {
  return (
    <div className="flex flex-col sm:flex-row items-center justify-center gap-3 py-8 px-6">
      <p className="text-gray-400 font-bold uppercase text-xs italic">
        No products yet
      </p>
      <button
        type="button"
        onClick={() => onAddProduct(manufacturerId)}
        className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-brand-50 text-brand-700
          font-black uppercase text-xs tracking-wider hover:bg-brand-100 transition-colors"
      >
        <Plus size={14} />
        Add product
      </button>
    </div>
  );
}

function CampaignBadge({ name }: { name?: string }) {
  return (
    <span
      className="text-xs font-black text-brand-600 bg-brand-50 px-3 py-1
        rounded-full uppercase tracking-tighter"
    >
      {name || 'Generic'}
    </span>
  );
}

function ActiveLabel({ active }: { active: boolean }) {
  return active ? (
    <span className="flex items-center gap-1 text-green-600 text-xs font-black uppercase">
      <CheckCircle2 size={13} /> Active
    </span>
  ) : (
    <span className="flex items-center gap-1 text-gray-400 text-xs font-black uppercase">
      <XCircle size={13} /> Inactive
    </span>
  );
}

function DesktopRow({
  product: p,
  expanded,
  onToggleExpand,
  renderActions,
  onVariantsChanged,
}: RowProps) {
  return (
    <React.Fragment>
      <tr
        className="hover:bg-gray-50 transition-colors cursor-pointer"
        onClick={() => onToggleExpand(p.id)}
      >
        <td className="pl-4 pr-2 py-4 w-8">
          {expanded ? (
            <ChevronDown size={16} className="text-brand-500" />
          ) : (
            <ChevronRight size={16} className="text-gray-300" />
          )}
        </td>
        <td className="px-2 py-4 font-bold text-gray-800">{p.name}</td>
        <td className="px-6 py-4">
          <CampaignBadge name={p.campaign_name} />
        </td>
        <td className="px-6 py-4">
          <ActiveLabel active={p.is_active} />
        </td>
        <td className="px-2 py-4">
          <EmailReadyBadge product={p} />
        </td>
        <td className="px-6 py-4 text-right" onClick={(e) => e.stopPropagation()}>
          {renderActions(p)}
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={6} className="p-0">
            <VariantPanel
              productId={p.id}
              productName={p.name}
              onVariantsChanged={onVariantsChanged}
            />
          </td>
        </tr>
      )}
    </React.Fragment>
  );
}

function MobileCard({
  product: p,
  expanded,
  onToggleExpand,
  renderActions,
  onVariantsChanged,
}: RowProps) {
  return (
    <div className="bg-white rounded-2xl border-2 border-gray-100 overflow-hidden">
      <div className="p-4">
        <div className="flex items-start justify-between mb-2">
          <div>
            <p className="font-black text-gray-900">{p.name}</p>
            <span className="text-xs font-black text-brand-600 bg-brand-50 px-2 py-0.5 rounded-full uppercase">
              {p.campaign_name || 'Generic'}
            </span>
          </div>
          {p.is_active ? (
            <span className="text-green-600 text-xs font-black uppercase bg-green-50 px-2 py-1 rounded-lg flex items-center gap-1">
              <CheckCircle2 size={11} /> Active
            </span>
          ) : (
            <span className="text-gray-400 text-xs font-black uppercase bg-gray-50 px-2 py-1 rounded-lg flex items-center gap-1">
              <XCircle size={11} /> Inactive
            </span>
          )}
        </div>
        <div className="mb-2">
          <EmailReadyBadge product={p} />
        </div>
        <div className="flex items-center justify-between pt-2 border-t border-gray-100">
          {renderActions(p)}
          <button
            type="button"
            onClick={() => onToggleExpand(p.id)}
            className="text-xs font-bold text-brand-600 hover:text-brand-700 flex items-center gap-1"
          >
            {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            Variants
          </button>
        </div>
      </div>
      {expanded && (
        <VariantPanel
          productId={p.id}
          productName={p.name}
          onVariantsChanged={onVariantsChanged}
        />
      )}
    </div>
  );
}

function GroupHeader({
  name,
  count,
  layout,
}: {
  name: string;
  count: number;
  layout: 'desktop' | 'mobile';
}) {
  const wrap =
    layout === 'desktop'
      ? 'flex items-center gap-2 px-6 py-3 bg-gray-50 border-y border-gray-100'
      : 'flex items-center gap-2 mb-2 px-1';
  return (
    <div className={wrap}>
      <Building2 size={layout === 'desktop' ? 14 : 13} className="text-brand-500" />
      <span className="text-xs font-black uppercase tracking-widest text-brand-700">
        {name}
      </span>
      <span className="text-xs text-gray-400">({count})</span>
    </div>
  );
}

function GroupBody({
  group,
  layout,
  expandedProductId,
  onToggleExpand,
  onAddProduct,
  renderActions,
  onVariantsChanged,
}: ManufacturerGroupProps) {
  const { manufacturerId, products } = group;
  if (products.length === 0) {
    const empty = <EmptyState manufacturerId={manufacturerId} onAddProduct={onAddProduct} />;
    return layout === 'mobile' ? (
      <div className="bg-white rounded-2xl border-2 border-dashed border-gray-200">{empty}</div>
    ) : empty;
  }
  const rowProps = { onToggleExpand, renderActions, onVariantsChanged };
  if (layout === 'desktop') {
    return (
      <table className="w-full text-left">
        <tbody className="divide-y divide-gray-50">
          {products.map((p) => (
            <DesktopRow
              key={p.id}
              product={p}
              expanded={expandedProductId === p.id}
              {...rowProps}
            />
          ))}
        </tbody>
      </table>
    );
  }
  return (
    <div className="space-y-2">
      {products.map((p) => (
        <MobileCard key={p.id} product={p} expanded={expandedProductId === p.id} {...rowProps} />
      ))}
    </div>
  );
}

export function ManufacturerGroup(props: ManufacturerGroupProps) {
  const { group, layout } = props;
  return (
    <div>
      <GroupHeader
        name={group.manufacturerName}
        count={group.products.length}
        layout={layout}
      />
      <GroupBody {...props} />
    </div>
  );
}
